import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import ManualRedemptionDialog from "@/pages/admin/ManualRedemptionDialog";
import Pager from "@/pages/admin/Pager";
import { openReceipt } from "@/lib/receipt";

const statusBadgeClass = (s) => {
  if (s === "paid") return "bg-pink-500/20 text-pink-300 border-pink-500/30";
  if (s === "approved") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
  if (s === "rejected") return "bg-red-500/20 text-red-300 border-red-500/30";
  return "bg-amber-500/20 text-amber-300 border-amber-500/30";
};

// Admin-side vocabulary for shop-scan redemptions.
const adminLabel = (r) => {
  if (r.type === "shop_scan") {
    if (r.status === "pending") return "Payment Pending";
    if (r.status === "paid") return "Paid";
    if (r.status === "rejected") return "Refunded";
  }
  return r.status;
};

export default function RedemptionsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [processing, setProcessing] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState("");
  const [adminNote, setAdminNote] = useState("");

  const load = useCallback(() => {
    const params = { limit, skip };
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/admin/redemptions", { params }).then((r) => {
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [statusFilter, limit, skip]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [statusFilter]);

  const upload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceiptUrl(data.url);
      toast.success("Receipt uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const process = async (status) => {
    if (!processing) return;
    try {
      await api.post(`/admin/redemptions/${processing.id}/process`, { status, receipt_url: receiptUrl || null, admin_note: adminNote || null });
      toast.success(`Marked as ${status}`);
      setProcessing(null); setReceiptUrl(""); setAdminNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <ManualRedemptionDialog onCreated={load} />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="select-redemption-status" className="velo-input w-48"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#22103F] border-white/10 text-white">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="velo-card overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-[#170929] border-b border-white/10">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-6 py-4">User</th>
              <th className="px-6 py-4">Type</th>
              <th className="px-6 py-4 text-right">Points</th>
              <th className="px-6 py-4 text-right">Amount</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Requested</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`redemption-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <td className="px-6 py-4 text-sm text-white">
                  {r.user_name}
                  {r.type === "shop_scan" && r.shop_name ? <div className="text-[11px] text-slate-500 mt-0.5">at {r.shop_name}</div> : null}
                </td>
                <td className="px-6 py-4 text-sm text-slate-300 capitalize">
                  {r.type === "shop_scan" ? "Shop scan" : r.type}
                  {r.redemption_code ? <span className="block text-xs font-mono text-pink-400">{r.redemption_code}</span> : null}
                </td>
                <td className="px-6 py-4 text-sm text-white text-right font-mono">{r.points}</td>
                <td className="px-6 py-4 text-sm text-white text-right font-mono">{r.amount > 0 ? `₹${r.amount}` : "-"}</td>
                <td className="px-6 py-4"><Badge className={`border capitalize ${statusBadgeClass(r.status)}`}>{adminLabel(r)}</Badge></td>
                <td className="px-6 py-4 text-xs text-slate-500">{format(new Date(r.created_at), "dd MMM yy, HH:mm")}</td>
                <td className="px-6 py-4 text-right">
                  {(r.type === "cash" || r.type === "shop_scan") && r.status === "pending" ? (
                    <Button data-testid={`btn-process-${r.id}`} size="sm" onClick={() => setProcessing(r)} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black h-8">Process</Button>
                  ) : null}
                  {r.receipt_url ? (
                    <button
                      type="button"
                      data-testid={`btn-admin-receipt-${r.id}`}
                      onClick={() => openReceipt(r.receipt_url)}
                      className="text-xs text-pink-400 hover:underline ml-2"
                    >
                      Receipt
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-500 text-sm">No redemptions</td></tr>}
          </tbody>
        </table>
        <Pager
          total={total}
          limit={limit}
          skip={skip}
          testidPrefix="pager-redemptions"
          onChange={({ limit: l, skip: s }) => { setLimit(l); setSkip(s); }}
        />
      </div>

      <Dialog open={!!processing} onOpenChange={() => { setProcessing(null); setReceiptUrl(""); setAdminNote(""); }}>
        <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>{processing?.type === "shop_scan" ? "Process shop redemption" : "Process cash payout"}</DialogTitle></DialogHeader>
          {processing ? (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="velo-surface p-3 rounded"><div className="text-xs text-slate-500 uppercase">User</div><div className="text-sm text-white mt-1">{processing.user_name}</div></div>
                <div className="velo-surface p-3 rounded"><div className="text-xs text-slate-500 uppercase">Amount</div><div className="text-sm text-white mt-1 font-mono">₹{processing.amount}</div></div>
              </div>
              {processing.type === "shop_scan" ? (
                <div className="velo-surface p-3 rounded"><div className="text-xs text-slate-500 uppercase">Shop</div><div className="text-sm text-white mt-1">{processing.shop_name}{processing.shop_location ? <span className="text-slate-500"> · {processing.shop_location}</span> : null}</div><div className="text-[11px] text-slate-500 font-mono mt-0.5">{processing.shop_id} · Code {processing.redemption_code}</div></div>
              ) : null}
              {processing.bank_snapshot ? (
                <div className="velo-surface p-4 rounded-lg">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Bank details {processing.type === "shop_scan" ? "(shop)" : ""}</div>
                  <div className="text-sm text-white">{processing.bank_snapshot.account_holder}</div>
                  <div className="text-xs text-slate-400 font-mono mt-1">{processing.bank_snapshot.bank_name} · {processing.bank_snapshot.account_number} · {processing.bank_snapshot.ifsc}</div>
                </div>
              ) : null}
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Payment receipt / evidence</Label>
                <Input data-testid="input-upload-receipt" type="file" onChange={upload} className="velo-input mt-2 file:text-white file:bg-transparent file:border-0" />
                {receiptUrl ? <div className="text-xs text-emerald-400 mt-2">Receipt attached</div> : null}
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Admin note</Label>
                <Input data-testid="input-admin-note" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="e.g. UTR reference" className="velo-input mt-2" />
              </div>
            </div>
          ) : null}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button data-testid="btn-reject-payout" onClick={() => process("rejected")} variant="outline" className="rounded-full border-red-500/30 text-red-400 bg-transparent hover:bg-red-500/10">Reject & refund points</Button>
            <Button data-testid="btn-mark-paid" onClick={() => process("paid")} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">Mark as paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
